import React from "react";
import styled from "styled-components";

const TargetRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
`;

const Label = styled.span`
  font-weight: 600;
  color: #1f2937;
  font-size: 0.9rem;
`;

const TargetChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  background: rgba(99, 102, 241, 0.16);
  color: #312e81;
  font-size: 0.85rem;
  font-weight: 600;
`;

interface TargetProps {
  targetType: string;
  targetValue: number | null | undefined;
  targetValueLow?: number | null | undefined;
  targetValueHigh?: number | null | undefined;
}

export const TargetSummary: React.FC<TargetProps> = ({
  targetType,
  targetValue,
  targetValueLow,
  targetValueHigh,
}) => {
  if (!targetType || targetType === "OPEN") {
    return null;
  }

  if (targetValue === null || targetValue === undefined) {
    return null;
  }

  const showRange =
    targetValueLow !== undefined &&
    targetValueHigh !== undefined &&
    targetValueLow !== null &&
    targetValueHigh !== null &&
    targetValueLow !== targetValueHigh;

  const primary = formatTargetValue(targetType, targetValue);
  const secondary = showRange
    ? `${formatTargetValue(targetType, targetValueLow!)} – ${formatTargetValue(targetType, targetValueHigh!)}`
    : null;

  return (
    <TargetRow>
      <Label>Target</Label>
      <TargetChip>
        {primary}
        {secondary && <span>{secondary}</span>}
      </TargetChip>
    </TargetRow>
  );
};

function formatTargetValue(type: string, value: number) {
  const normalized = type.toUpperCase();

  switch (normalized) {
    case "PACE":
      return metresPerSecondToMinutesPerKilometre(value);
    case "PACE_ZONE":
      return `Zone ${Math.round(value)}`;
    case "POWER_GREATER_THAN":
    case "POWER_LESS_THAN":
    case "POWER":
      return `${Math.round(value)} W`;
    case "HEART_RATE":
    case "HEART_RATE_GREATER_THAN":
    case "HEART_RATE_LESS_THAN":
      return `${Math.round(value)} bpm`;
    case "SPEED":
      return `${(value * 3.6).toFixed(1)} km/h`;
    case "CADENCE":
      return `${Math.round(value)} spm`;
    default:
      return value.toString();
  }
}

function metresPerSecondToMinutesPerKilometre(value: number) {
  if (value <= 0) {
    return "—";
  }
  const secondsPerKilometre = 1000 / value;
  const minutes = Math.floor(secondsPerKilometre / 60);
  const seconds = Math.round(secondsPerKilometre % 60);
  return `${minutes}:${seconds.toString().padStart(2, "0")}/km`;
}
